// Import Core
import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
// Import Libraries
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
// Import Components
import Button from '../ui/buttons/Button';
import TextButton from '../ui/buttons/TextButton';
// Import Utils
import { usePlaceholderImage } from '../../utils/useRandomPlaceholderImage';
import { getSlug } from '../../utils/getSlug';
import Stars from '../ui/Stars';
import { toSlug } from '../../utils/search/toSlug';

const CardListShortBreaksStyling = styled.div`
	display: flex;
	padding: var(--global-padding);
	flex-direction: column;
	align-items: center;
	gap: 64px;
	width: 100%;
	max-width: 1440px;
	margin: 0 auto;
	@media (max-width: 768px) {
		padding: var(--global-padding-mobile);
		align-items: flex-start;
		gap: 32px;
	}
	p.location{
		font-weight:700;
		color: var(--neutral-75);
	}
	.section-title {
		display: flex;
		justify-content: space-between;
		width: 100%;
		/* padding: 0 64px 0 24px; */
		@media (max-width: 768px) {
			/* padding: 0 20px 0 0; */
			flex-direction: column;
			gap: 24px;
		}
		.content {
			display: flex;
			max-width: 768px;
			flex-direction: column;
			align-items: flex-start;
			gap: 12px;
			@media (max-width: 768px) {
				gap: 20px;
			}
			.title {
				color: var(--black);
			}
		}
	}

	.card-list-items {
		display: flex;
		flex-wrap: wrap;
		row-gap: 20px;
		align-content: flex-start;
		width: 100%;
		@media (max-width: 768px) {
			flex-direction: column;
			row-gap: 32px;
		}

		.card {
			/* flex: 25%; */
			padding: 0 32px;
			border-right: 1px solid var(--neutral-10);
			display: flex;
			gap: 24px;
			max-width: 280px;
			flex-direction: column;
			width: 100%;
			&:first-child {
				padding: 0 32px 20px 0;
			}
			&:last-child {
				padding-right: 0;
				border-right: none;
			}
			flex: calc(50% - 10px);
			position: relative;
			margin-bottom: 20px;
			&:before {
				content: '';
				position: absolute;
				bottom: -20px;
				left: 0;
				height: 1px;
				width: 100%;
				border-bottom: 1px solid var(--neutral-10);
			}
      &:nth-last-child(2) {
        &:before {
          border-bottom: unset;
        }
      }
			@media (max-width: 768px) {
				margin-bottom: 0;
				&:before {
					content: unset;
				}
			}
			/* &:nth-child(odd) {
				padding-left: 0;
			} */
			&:nth-child(even) {
				padding-right: 0;
				border-right: none;
				padding-left: 32px;
				@media (max-width: 768px) {
					padding-left: 0;
				}
			}
			@media (max-width: 1200px) {
				flex-direction: column;
			}
			@media (max-width: 768px) {
				flex: unset;
				gap: 32px;
				border-right: none;
				border-bottom: 1px solid var(--neutral-10);
				padding: 0 0px 32px 0;
				&:first-child,
				&:nth-child(2) {
					margin-bottom: 0px;
					padding: 0 0px 32px 0;
					&:before {
						content: none;
					}
				}
				&:last-child, &:nth-last-child(2) {
					padding-bottom: 0;
					border-bottom: none;
				}
			}

      .intro {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
		gap: 10px;

		.title {
			font-size: var(--h4-font-size)
		}
        .ratings {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          .star-rating {
            display: flex;
            .star {
              width: 18.5px;
              height: 17.6px;
            }
          }
          .hotel-type {
            text-transform: capitalize;
          }
        }
      }
			.content {
				display: flex;
				flex-direction: column;
				align-items: flex-start;
				gap: 16px;
				flex: 1;

				@media (max-width: 768px) {
					gap: 12px;
				}
				.card-section-title {
					display: flex;
					flex-direction: column;
					align-items: flex-start;
					gap: 8px;
					.subheading {
						display: flex;
						justify-content: space-between;
						align-items: center;
						width: 100%;
						color: var(--neutral-75);
						gap: 8px;
						span {}
					}
				}
				.read-more-btn {
					padding-top: 8px;
					margin-top: auto;
					@media (max-width: 768px) {
						padding-top: 4px;
					}
				}
			}
			.card-image {
				width: fit-content;
				height: 200px;
				@media (max-width: 1200px) {
					width: 100%;
				}
				.gatsby-image-wrapper,
				img {
					object-fit: cover;
					width: inherit;
					height: inherit;
					max-height: inherit;
				}
				.placeholder-img {
					object-fit: contain;
				}
			}
		}
	}
`;

const CardListShortBreaks = ({
	addToAnchor,
	subtitle,
	title,
	content,
	linkText,
	link,
	cardList,
}) => {
  const slugPath = link && link.link ? (typeof link === 'string' ? link : link.link.slug.current) : null;
	const { randomPlaceholderImage } = usePlaceholderImage();

	return (
		<CardListShortBreaksStyling
			id={addToAnchor ? `anchor_${toSlug(title)}` : ''}
			className={`card-list ${addToAnchor && 'anchor-link'}`}>
			<div className='section-title'>
				<div className='content'>
					{subtitle && <p className='tagline'>{subtitle}</p>}
					<h2 className='title'>{title}</h2>
					{content && (<p className='content'>{content}</p>)}
				</div>
				{slugPath && (
					<Link to={slugPath}>
						<Button
							size='md'
							btnStyle='outline'
							className=''
							value={linkText}
							clickHandler={false}
							isDisabled={false}
						/>
					</Link>
				)}
			</div>
			<div className='card-list-items'>
				{cardList.map((card, index) => (
					<div className='card' key={`card-${index}`}>
            <div className="intro">
								<h4 className='alt title'>{card.title}</h4>
                {(card.starRating || card.hotelType) && (
                  <div className="ratings">
                    {card.starRating && (
                      <div className="star-rating">
                        <Stars starRating={card.starRating} />
                      </div>
                    )}
                    {card.hotelType && card.hotelType!== 'none' && (
                      <p className="hotel-type bold italic red">{card.hotelType}</p>
                    )}
                  </div>
                )}
            </div>
						<div className='card-image'>
							{card.image && card.image.asset ? (
								<GatsbyImage image={getImage(card.image.asset)} alt={title} />
							) : (
								<img
									src={randomPlaceholderImage}
									alt={title}
									className='placeholder-img'
								/>
							)}
						</div>

						<div className='content'>
							<div className='card-section-title'>
								{card.tagline && (
									<p className='tagline small'>{card.tagline}</p>
								)}
                {card.city && card.country && (
                  <p className='location'>{`${card.city.name}, ${card.country.name}`}</p>
                )}
							</div>
							<p className='medium-excerpt'>{card.content}</p>
							<Link to={getSlug(card)} className='read-more-btn'>
								<TextButton
									size='md'
									className=''
									value={'Read More'}
									clickHandler={false}
									isDisabled={false}
								/>
							</Link>
						</div>
					</div>
				))}
			</div>
		</CardListShortBreaksStyling>
	);
};

export default CardListShortBreaks;
